import React, { createContext, useContext, useState, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface UploadContextValue {
  /** Current session / sequence UUID used for the active upload & reconstruction */
  sequenceUuid: string | null;
  /** Explicitly set the UUID (rarely used – prefer mintNewSequenceUuid) */
  setSequenceUuid: (uuid: string) => void;
  /** Generate a brand-new UUID and store it */
  mintNewSequenceUuid: () => string;
  /** Reset all context fields so the next flow starts from scratch */
  reset: () => void;
}

const UploadContext = createContext<UploadContextValue | undefined>(undefined);

export const UploadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sequenceUuid, setSequenceUuidState] = useState<string | null>(null);

  /** Direct setter – mainly for tests/debugging */
  const setSequenceUuid = (uuid: string) => setSequenceUuidState(uuid);

  /** Mint a new UUID and save it */
  const mintNewSequenceUuid = () => {
    const newId = uuidv4();
    setSequenceUuidState(newId);
    return newId;
  };

  /** Clear all state for a brand-new flow */
  const reset = () => setSequenceUuidState(null);

  return (
    <UploadContext.Provider value={{ sequenceUuid, setSequenceUuid, mintNewSequenceUuid, reset }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUploadCtx = (): UploadContextValue => {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error('useUploadCtx must be used within an <UploadProvider>');
  }
  return ctx;
}; 